import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api, MeData } from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { Toggle } from '../components/ui/Toggle'
import { RowSkeleton } from '../components/ui/Skeleton'

// Количество включённых тумблеров, доступных на текущем тарифе.
export function countEnabledNotifications(me: MeData): number {
  const isProPlus = me.subscription.tier === 'pro'
  const toggles: boolean[] = [
    isProPlus && me.morning_brief_enabled,
    me.evening_checkin_enabled,
    isProPlus && me.mail_scan_enabled,
    me.notif_facts,
    me.notif_events,
    me.notif_expiry,
    me.notif_digest,
    me.notif_referral,
    me.notif_limit_warning,
  ]
  return toggles.filter(Boolean).length
}

// ── Notifications page ──────────────────────────────────────────────────────────

export function Notifications() {
  const qc = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    staleTime: 60_000,
  })

  const briefMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch('/api/me/brief', { morning_brief_enabled: enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })

  const notifMutation = useMutation({
    mutationFn: (patch: Partial<Pick<MeData, 'notif_expiry' | 'notif_digest' | 'notif_referral' | 'notif_limit_warning' | 'notif_facts' | 'notif_events' | 'evening_checkin_enabled' | 'mail_scan_enabled'>>) =>
      api.patch('/api/me', patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })

  const isProPlus =
    data?.subscription.tier === 'pro'

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3">
        {/* Назад — в настройки, а не в профиль: уведомления живут внутри них,
            и «назад» обязано вести туда, откуда пришли. */}
        <Link
          to="/settings"
          className="btn-active inline-flex items-center gap-1 text-sm text-ink-1 mb-2"
        >
          <ChevronLeft size={16} /> Настройки
        </Link>
        <p className="text-h1 text-ink">Уведомления</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-5">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {error && <ErrorCard onRetry={refetch} />}

        {data && (
          <>
            {/* ── Ежедневно ── */}
            <section>
              <p className="section-label">Ежедневно</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                {/* Утренний разбор дня — только Pro */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className={`text-base ${isProPlus ? 'text-ink' : 'text-ink-2'}`}>
                      Утренний разбор дня
                    </p>
                    <p className="text-xs text-ink-2 mt-0.5">
                      {isProPlus
                        ? 'Что горит, встречи и окна между ними — в 8:00'
                        : 'Доступно на Pro'}
                    </p>
                  </div>
                  <Toggle
                    checked={!!isProPlus && data.morning_brief_enabled}
                    disabled={!isProPlus || briefMutation.isPending}
                    onChange={() => briefMutation.mutate(!data.morning_brief_enabled)}
                  />
                </div>
                {/* Напоминания о событиях */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Напоминать о событиях</p>
                    <p className="text-xs text-ink-2 mt-0.5">
                      За 15 минут до начала. На весь день — утром в 9:00
                    </p>
                  </div>
                  <Toggle
                    checked={data.notif_events}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ notif_events: !data.notif_events })}
                  />
                </div>
                {/* Вечерний чекин */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Вечерний чекин</p>
                    <p className="text-xs text-ink-2 mt-0.5">
                      Открытые задачи и планы на завтра — в 20:00
                    </p>
                  </div>
                  <Toggle
                    checked={data.evening_checkin_enabled}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ evening_checkin_enabled: !data.evening_checkin_enabled })}
                  />
                </div>
              </div>
            </section>

            {/* ── Автоматически ── */}
            <section>
              <p className="section-label">Автоматически</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                {/* Договорённости в почте — только Pro */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className={`text-base ${isProPlus ? 'text-ink' : 'text-ink-2'}`}>
                      Искать договорённости в почте
                    </p>
                    <p className="text-xs text-ink-2 mt-0.5">
                      {isProPlus
                        ? 'Проверяю входящие и предлагаю напоминания'
                        : 'Доступно на Pro'}
                    </p>
                  </div>
                  <Toggle
                    checked={!!isProPlus && data.mail_scan_enabled}
                    disabled={!isProPlus || notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ mail_scan_enabled: !data.mail_scan_enabled })}
                  />
                </div>
                {/* Новые факты */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Новые факты</p>
                    <p className="text-xs text-ink-2 mt-0.5">
                      Когда я запоминаю что-то о тебе
                    </p>
                  </div>
                  <Toggle
                    checked={data.notif_facts}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ notif_facts: !data.notif_facts })}
                  />
                </div>
              </div>
            </section>

            {/* ── Сервисные ── */}
            <section>
              <p className="section-label">Сервисные</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                {/* Истечение подписки */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Истечение подписки</p>
                    <p className="text-xs text-ink-2 mt-0.5">За 3 и 1 день до конца</p>
                  </div>
                  <Toggle
                    checked={data.notif_expiry}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ notif_expiry: !data.notif_expiry })}
                  />
                </div>
                {/* Еженедельный дайджест */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Еженедельный дайджест</p>
                    <p className="text-xs text-ink-2 mt-0.5">Итоги недели по воскресеньям</p>
                  </div>
                  <Toggle
                    checked={data.notif_digest}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ notif_digest: !data.notif_digest })}
                  />
                </div>
                {/* Рефералы */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Рефералы</p>
                    <p className="text-xs text-ink-2 mt-0.5">Когда друг присоединится или оплатит</p>
                  </div>
                  <Toggle
                    checked={data.notif_referral}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ notif_referral: !data.notif_referral })}
                  />
                </div>
                {/* Предупреждение о лимите */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-base text-ink">Предупреждение о лимите</p>
                    <p className="text-xs text-ink-2 mt-0.5">Когда останется мало сообщений</p>
                  </div>
                  <Toggle
                    checked={data.notif_limit_warning}
                    disabled={notifMutation.isPending}
                    onChange={() => notifMutation.mutate({ notif_limit_warning: !data.notif_limit_warning })}
                  />
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
